import React, { useState, useEffect, useContext } from 'react'

import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

import PopGroupsGrid from './popGroupsGrid';
import PopGroupsModal from './popGroupsModal';

import { VIEW, UPDATE, DELETE, NEW } from '../../assets/Constants/actionsTypes';
import Notifications, { ERROR_NOTIFICATION, SUCCESS_NOTIFICATION } from '../../helpers/notifications';
import { BEN_UNIT_HOUSEHOLDS } from './costants';
import { useTranslation } from 'react-i18next';

import API from "../../services/api";
import validator from 'validator';
import AuthContext from '../../context/Auth/AuthContext';

const PopGroups = () => {
    // * we call the hook to make translations
    const { t } = useTranslation('common');
    // Modal Window type = DELETE, NEW, VIEW or UPDATE
    const [viewType, setViewType] = useState('');
    // Single component
    const [popGroupData, setpopGroupData] = useState({});
    /**State of the modal window. True = showing ... False = hidden */
    const [showModal, setShowModal] = useState(false);
    /** State of the dialog. True = showing ... False = hidden */
    const [showAlert, setShowAlert] = useState(false);
    // List of groups. See useEffect
    const [groupsList, setGroupsList] = useState([]);
    // Session context to get the current institution data
    const { get_currentInstitution } = useContext(AuthContext);

    // we use useEffect to call the function that fills the grid
    useEffect(() => {
        getPopGroupsList();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    // Function that updates the groupsList variable in the state. This list will be used to fill the grid
    const getPopGroupsList = async () => {
        setGroupsList([]);
        try {
            // data from API
            const response = await API.get(`/populationgroups/getAll?institutionId=${get_currentInstitution().catInstitutionId}`);
            // Changing References population and sex ID to value. This value will appear in the grid
            response.data.data.forEach((element) => {
                // Ex: totalPopulation comes from DB, here we chaange that by Total population
                const refpopulation = t(`referencePopMatrixCode.${element.referencePopMatrixCode}`);
                // to show in "View-Update" modal
                element.refPopId = element.referencePopMatrixCode;
                // To show in grid
                element.referencePopMatrixCode = refpopulation;
                // Sex changing value
                if (element.includedFemales && !element.includedMales) {
                    element.sex = t('modals.sex_f'); // to show in grid
                    element.sexId = 1; // to show in "View-Update" modal
                } else if (!element.includedFemales && element.includedMales) {
                    element.sex = t('modals.sex_m'); // to show in grid
                    element.sexId = 0; // to show in "View-Update" modal
                } else {
                    element.sex = t('modals.sex_b'); // to show in grid
                    element.sexId = 2; // to show in "View-Update" modal
                }
                // Elegibility Criteria concatenation to show on grid
                const ec2 = element.elegibilityCriteria2 === null ? '' : element.elegibilityCriteria2 + ', ';
                const ec3 = element.elegibilityCriteria3 === null ? '' : element.elegibilityCriteria3
                if (ec2 === '' && ec3 === '')
                    element.ecTotal = `${element.elegibilityCriteria1}`
                else
                    element.ecTotal = `${element.elegibilityCriteria1}, ${ec2} ${ec3}`
            });
            setGroupsList(response.data.data);
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e);
        }
    }

    // Triggered when the user clicks an action in the grid. Opens the modal window according to the selection
    const onActionClick = (actionItem, dataItme) => {
        const action = actionItem.value;
        setViewType(action);
        setpopGroupData(dataItme);

        switch (action) {
            case NEW:
                toggleWindow();
                break;
            case VIEW:
                toggleWindow();
                break;
            case UPDATE:
                toggleWindow();
                break;
            case DELETE:
                toggleAlert();
                break;
            default:
                break;
        }
    }

    // Show or hide the modal window. See state variable
    const toggleWindow = () => {
        setShowModal(!showModal);
    }

    // Show or hide the alert dialog. See state variable
    const toggleAlert = () => {
        setShowAlert(!showAlert);
    }

    // The upsert call is handled here
    const onSave = async (data) => {
        try {
            // Validations
            // * Name Validation
            if (validator.isEmpty(data.name)) {
                Notifications.notify(t('popGroups.name_required'), ERROR_NOTIFICATION);
                return;
            }
            // * HouseHolds validations
            if (data.beneficaryUnit === BEN_UNIT_HOUSEHOLDS) {
                // * Elegibility Criteria 1 validations
                if (validator.isEmpty(data.ec1Desc) || validator.isEmpty(data.ec1HH.toString())) {
                    Notifications.notify(t('popGroups.eC1_required'), ERROR_NOTIFICATION);
                    return;
                } else if (!validator.isEmpty(data.ec1Desc) && !validator.isFloat(data.ec1HH.toString())) {
                    Notifications.notify(t('popGroups.ec1_y1_number'), ERROR_NOTIFICATION);
                    return;
                } else if (data.ec1HH < 0 || data.ec1HH > 100) {
                    Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                    return;
                }
                // * Elegibility Criteria 2 validations
                if (!validator.isEmpty(data.ec2Desc) && validator.isEmpty(data.ec2HH.toString())) {
                    Notifications.notify(t('popGroups.ec2_no_y0'), ERROR_NOTIFICATION);
                    return;
                } else if (!validator.isEmpty(data.ec2Desc) && !validator.isFloat(data.ec2HH.toString())) {
                    Notifications.notify(t('popGroups.ec2_y1_number'), ERROR_NOTIFICATION);
                    return;
                } else if (data.ec2HH < 0 || data.ec2HH > 100) {
                    Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                    return;
                } else if (validator.isEmpty(data.ec2Desc) && !validator.isEmpty(data.ec2HH.toString())) {
                    Notifications.notify(t('popGroups.ec2_required'), ERROR_NOTIFICATION);
                    return;
                }
                // * Elegibility Criteria 3 validations
                if (!validator.isEmpty(data.ec3Desc) && validator.isEmpty(data.ec3HH.toString())) {
                    Notifications.notify(t('popGroups.ec3_no_y0'), ERROR_NOTIFICATION);
                    return;
                } else if (!validator.isEmpty(data.ec3Desc) && !validator.isFloat(data.ec3HH.toString())) {
                    Notifications.notify(t('popGroups.ec3_y1_number'), ERROR_NOTIFICATION);
                    return;
                } else if (data.ec3HH < 0 || data.ec3HH > 100) {
                    Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                    return;
                } else if (validator.isEmpty(data.ec3Desc) && !validator.isEmpty(data.ec3HH.toString())) {
                    Notifications.notify(t('popGroups.ec3_required'), ERROR_NOTIFICATION);
                    return;
                }
            } else {
                if (data.selectedSex === 0) {
                    // * Male validations
                    if (validator.isEmpty(data.initialAgeMale.toString()) || validator.isEmpty(data.finalAgeMale.toString())) {
                        Notifications.notify(t('popGroups.ages_required'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isInt(data.initialAgeMale.toString()) || !validator.isInt(data.finalAgeMale.toString())) {
                        Notifications.notify(t('popGroups.ages_number'), ERROR_NOTIFICATION);
                        return;
                    }
                    // Ages range
                    if((data.initialAgeMale < 0 || data.initialAgeMale > 100) 
                        || (data.finalAgeMale < 0 || data.finalAgeMale > 100)){
                        Notifications.notify(t('popGroups.ages_range'), ERROR_NOTIFICATION);
                        return;
                    }
                    // Age correct
                    if(data.initialAgeMale > data.finalAgeMale) {
                        Notifications.notify(t('popGroups.ages_correct_format'), ERROR_NOTIFICATION);
                        return;
                    }

                    // * Elegibility Criteria 1 validations
                    if (validator.isEmpty(data.ec1Desc) || validator.isEmpty(data.ec1Y1.toString())) {
                        Notifications.notify(t('popGroups.eC1_required'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec1Desc) && !validator.isFloat(data.ec1Y1.toString())) {
                        Notifications.notify(t('popGroups.ec1_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if (data.ec1Y1 < 0 || data.ec1Y1 > 100) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    }
                    // * Elegibility Criteria 2 validations
                    if (!validator.isEmpty(data.ec2Desc) && validator.isEmpty(data.ec2Y1.toString())) {
                        Notifications.notify(t('popGroups.ec2_no_y0'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec2Desc) && !validator.isFloat(data.ec2Y1.toString())) {
                        Notifications.notify(t('popGroups.ec2_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if (data.ec2Y1 < 0 || data.ec2Y1 > 100) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    } else if (validator.isEmpty(data.ec2Desc) && !validator.isEmpty(data.ec2Y1.toString())) {
                        Notifications.notify(t('popGroups.ec2_required'), ERROR_NOTIFICATION);
                        return;
                    }
                    // * Elegibility Criteria 3 validations
                    if (!validator.isEmpty(data.ec3Desc) && validator.isEmpty(data.ec3Y1.toString())) {
                        Notifications.notify(t('popGroups.ec3_no_y0'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec3Desc) && !validator.isFloat(data.ec3Y1.toString())) {
                        Notifications.notify(t('popGroups.ec3_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if (data.ec3Y1 < 0 || data.ec3Y1 > 100) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    } else if (validator.isEmpty(data.ec3Desc) && !validator.isEmpty(data.ec3Y1.toString())) {
                        Notifications.notify(t('popGroups.ec3_required'), ERROR_NOTIFICATION);
                        return;
                    }
                } else if (data.selectedSex === 1) {
                    // * Male validations
                    if (validator.isEmpty(data.initialAgeFemale.toString()) || validator.isEmpty(data.finalAgeFemale.toString())) {
                        Notifications.notify(t('popGroups.ages_required'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isInt(data.initialAgeFemale.toString()) || !validator.isInt(data.finalAgeFemale.toString())) {
                        Notifications.notify(t('popGroups.ages_number'), ERROR_NOTIFICATION);
                        return;
                    }
                    // Ages range
                    if((data.initialAgeFemale < 0 || data.initialAgeFemale > 100) 
                        || (data.finalAgeFemale < 0 || data.finalAgeFemale > 100)){
                        Notifications.notify(t('popGroups.ages_range'), ERROR_NOTIFICATION);
                        return;
                    }
                    // Ages correct
                    if(data.initialAgeFemale > data.finalAgeFemale) {
                        Notifications.notify(t('popGroups.ages_correct_format'), ERROR_NOTIFICATION);
                        return;
                    }

                    // * Elegibility Criteria 1 validations
                    if (validator.isEmpty(data.ec1Desc) || validator.isEmpty(data.ec1Y1.toString())) {
                        Notifications.notify(t('popGroups.eC1_required'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec1Desc) && !validator.isFloat(data.ec1Y1.toString())) {
                        Notifications.notify(t('popGroups.ec1_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if (data.ec1Y1 < 0 || data.ec1Y1 > 100) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    }
                    // * Elegibility Criteria 2 validations
                    if (!validator.isEmpty(data.ec2Desc) && validator.isEmpty(data.ec2Y1.toString())) {
                        Notifications.notify(t('popGroups.ec2_no_y0'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec2Desc) && !validator.isFloat(data.ec2Y1.toString())) {
                        Notifications.notify(t('popGroups.ec2_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if (data.ec2Y1 < 0 || data.ec2Y1 > 100) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    } else if (validator.isEmpty(data.ec2Desc) && !validator.isEmpty(data.ec2Y1.toString())) {
                        Notifications.notify(t('popGroups.ec2_required'), ERROR_NOTIFICATION);
                        return;
                    }
                    // * Elegibility Criteria 3 validations
                    if (!validator.isEmpty(data.ec3Desc) && validator.isEmpty(data.ec3Y1.toString())) {
                        Notifications.notify(t('popGroups.ec3_no_y0'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec3Desc) && !validator.isFloat(data.ec3Y1.toString())) {
                        Notifications.notify(t('popGroups.ec3_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if (data.ec3Y1 < 0 || data.ec3Y1 > 100) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    } else if (validator.isEmpty(data.ec3Desc) && !validator.isEmpty(data.ec3Y1.toString())) {
                        Notifications.notify(t('popGroups.ec3_required'), ERROR_NOTIFICATION);
                        return;
                    }
                } else if (data.selectedSex === 2) {
                    // * Male validations
                    if (validator.isEmpty(data.initialAgeFemale.toString()) || validator.isEmpty(data.finalAgeFemale.toString()) || validator.isEmpty(data.initialAgeMale.toString()) || validator.isEmpty(data.finalAgeMale.toString())) {
                        Notifications.notify(t('popGroups.ages_required'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isInt(data.initialAgeFemale.toString()) || !validator.isInt(data.finalAgeFemale.toString()) || !validator.isInt(data.initialAgeMale.toString()) || !validator.isInt(data.finalAgeMale.toString())) {
                        Notifications.notify(t('popGroups.ages_number'), ERROR_NOTIFICATION);
                        return;
                    }

                    if((data.initialAgeFemale < 0 || data.initialAgeFemale > 100) 
                        || (data.finalAgeFemale < 0 || data.finalAgeFemale > 100)){
                        Notifications.notify(t('popGroups.ages_range'), ERROR_NOTIFICATION);
                        return;
                    }

                    if((data.initialAgeMale < 0 || data.initialAgeMale > 100) 
                        || (data.finalAgeMale < 0 || data.finalAgeMale > 100)){
                        Notifications.notify(t('popGroups.ages_range'), ERROR_NOTIFICATION);
                        return;
                    }

                    // Ages correct
                    if(data.initialAgeFemale > data.finalAgeFemale) {
                        Notifications.notify(t('popGroups.ages_correct_format'), ERROR_NOTIFICATION);
                        return;
                    }

                    if(data.initialAgeMale > data.finalAgeMale) {
                        Notifications.notify(t('popGroups.ages_correct_format'), ERROR_NOTIFICATION);
                        return;
                    }

                    // * Elegibility Criteria 1 validations
                    if (validator.isEmpty(data.ec1Desc) || validator.isEmpty(data.ec1Y1.toString()) || validator.isEmpty(data.ec1Y2.toString())) {
                        Notifications.notify(t('popGroups.eC1_required'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec1Desc) && (!validator.isFloat(data.ec1Y1.toString()) || !validator.isFloat(data.ec1Y2.toString()))) {
                        Notifications.notify(t('popGroups.ec1_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if ((data.ec1Y1 < 0 || data.ec1Y1 > 100) || (data.ec1Y2 < 0 || data.ec1Y2 > 100)) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    }
                    // * Elegibility Criteria 2 validations
                    if (!validator.isEmpty(data.ec2Desc) && (validator.isEmpty(data.ec2Y1.toString()) || validator.isEmpty(data.ec2Y2.toString()))) {
                        Notifications.notify(t('popGroups.ec2_no_y0'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec2Desc) && (!validator.isFloat(data.ec2Y1.toString()) || !validator.isFloat(data.ec2Y2.toString()))) {
                        Notifications.notify(t('popGroups.ec2_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if ((data.ec2Y1 < 0 || data.ec2Y1 > 100) || (data.ec2Y2 < 0 || data.ec2Y2 > 100)) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    } else if (validator.isEmpty(data.ec2Desc) && (!validator.isEmpty(data.ec2Y1.toString()) || !validator.isEmpty(data.ec2Y2.toString()))) {
                        Notifications.notify(t('popGroups.ec2_required'), ERROR_NOTIFICATION);
                        return;
                    }
                    // * Elegibility Criteria 3 validations
                    if (!validator.isEmpty(data.ec3Desc) && (validator.isEmpty(data.ec3Y1.toString()) || validator.isEmpty(data.ec3Y2.toString()))) {
                        Notifications.notify(t('popGroups.ec3_no_y0'), ERROR_NOTIFICATION);
                        return;
                    } else if (!validator.isEmpty(data.ec3Desc) && (!validator.isFloat(data.ec3Y1.toString()) || !validator.isFloat(data.ec3Y2.toString()))) {
                        Notifications.notify(t('popGroups.ec3_y1_number'), ERROR_NOTIFICATION);
                        return;
                    } else if ((data.ec3Y1 < 0 || data.ec3Y1 > 100) || (data.ec3Y2 < 0 || data.ec3Y2 > 100)) {
                        Notifications.notify(t('popGroups.yearZero_range'), ERROR_NOTIFICATION);
                        return;
                    } else if (validator.isEmpty(data.ec3Desc) && (!validator.isEmpty(data.ec3Y1.toString()) || !validator.isEmpty(data.ec3Y2.toString()))) {
                        Notifications.notify(t('popGroups.ec3_required'), ERROR_NOTIFICATION);
                        return;
                    }
                }
            }

            // Variables to set the value of selectedSex according to API 
            let selectedFemale = true;
            let selectedMale = true;
            // Variables to set the female value
            let femEC1 = null;
            let femEC2 = null;
            let femEC3 = null;

            // * Sex selection
            if (data.selectedSex === 0) {
                // Selected => Male
                selectedFemale = false;
                selectedMale = true;
            } else if (data.selectedSex === 1) {
                // Selected => Female
                selectedFemale = true;
                selectedMale = false;

                // toggle value
                femEC1 = validator.isEmpty(data.ec1Y1.toString()) ? null : data.ec1Y1;
                femEC2 = validator.isEmpty(data.ec2Y1.toString()) ? null : data.ec2Y1;
                femEC3 = validator.isEmpty(data.ec3Y1.toString()) ? null : data.ec3Y1;

            } else if (data.selectedSex === 2) {
                // Selected => Both
                selectedFemale = true;
                selectedMale = true;
            } else {
                // Selected => HOUSEHOLDS
                selectedFemale = false;
                selectedMale = false;
            }

            //* Setting data in the correct format to sent via API
            //* Important: There are no problems with null values because the DB supports them 
            //* and the model in C# has the value types as nulleables
            //! NOTICE:
            //!        Eligibility Criteria Incidence Percentage is divided by 100 
            //!        because the model requires a probability value (between 0 and 1) 
            data = {
                popGroupId: data.popGroupId,
                institutionId: get_currentInstitution().catInstitutionId,
                name: validator.trim(data.name),
                includedFemales: selectedFemale,
                includedMales: selectedMale,
                referencePopMatrixCode: data.selectedRefPopulation,
                beneficiaryUnit: data.beneficaryUnit,
                minAgeMale: validator.isEmpty(data.initialAgeMale.toString()) ? null : data.initialAgeMale,
                maxAgeMale: validator.isEmpty(data.finalAgeMale.toString()) ? null : data.finalAgeMale,
                minAgeFemale: validator.isEmpty(data.initialAgeFemale.toString()) ? null : data.initialAgeFemale,
                maxAgeFemale: validator.isEmpty(data.finalAgeFemale.toString()) ? null : data.finalAgeFemale,

                elegibilityCriteria1: validator.trim(data.ec1Desc),
                elegibilityCriteria1Y0_HH: validator.isEmpty(data.ec1HH.toString()) ? null : data.ec1HH / 100,
                elegibilityCriteria1Y0_Male: data.selectedSex === 1 ? null : validator.isEmpty(data.ec1Y1.toString()) ? null : data.ec1Y1 / 100,
                elegibilityCriteria1Y0_Female: data.selectedSex === 1 ? femEC1 / 100 : validator.isEmpty(data.ec1Y2.toString()) ? null : data.ec1Y2 / 100,

                elegibilityCriteria2: validator.isEmpty(data.ec2Desc) ? null : validator.trim(data.ec2Desc),
                elegibilityCriteria2Y0_HH: validator.isEmpty(data.ec2HH.toString()) ? null : data.ec2HH / 100,
                elegibilityCriteria2Y0_Male: data.selectedSex === 1 ? null : validator.isEmpty(data.ec2Y1.toString()) ? null : data.ec2Y1 / 100,
                elegibilityCriteria2Y0_Female: data.selectedSex === 1 ? femEC2 / 100 : validator.isEmpty(data.ec2Y2.toString()) ? null : data.ec2Y2 / 100,

                elegibilityCriteria3: validator.isEmpty(data.ec3Desc) ? null : validator.trim(data.ec3Desc),
                elegibilityCriteria3Y0_HH: validator.isEmpty(data.ec3HH.toString()) ? null : data.ec3HH / 100,
                elegibilityCriteria3Y0_Male: data.selectedSex === 1 ? null : validator.isEmpty(data.ec3Y1.toString()) ? null : data.ec3Y1 / 100,
                elegibilityCriteria3Y0_Female: data.selectedSex === 1 ? femEC3 / 100 : validator.isEmpty(data.ec3Y2.toString()) ? null : data.ec3Y2 / 100
            }


            // * call the api to do the upsert
            const res = await API.post('/populationgroups/upsert', data);

            // * if errors exist, it is displayed as a notification
            if (res.data.code !== 100) {
                Notifications.notify(t(`popGroups.${res.data.code}`), ERROR_NOTIFICATION);
                return;
            }

            // * we decide what kind of message of success we should give
            const msg = data.popGroupId === 0 ? t('popGroups.new_message') : t('popGroups.update_message');

            // * hide the modal, shows success message and refresh the grid
            toggleWindow();
            Notifications.notify(msg, SUCCESS_NOTIFICATION);
            getPopGroupsList();

        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    //* When user clicks ok in delete modal
    const onDelete = async () => {
        try {
            // * call the api to do the delete
            const res = await API.delete(`/populationgroups?popGroupId=${popGroupData.popGroupId}`);

            // * if errors exist, it is displayed as a notification
            if (res.data.code !== 100) {
                Notifications.notify(t(`popGroups.${res.data.code}`), ERROR_NOTIFICATION);
                return;
            }

            // * hide the alert and refresh the grid
            toggleAlert();
            Notifications.notify(t('popGroups.delete_message'), SUCCESS_NOTIFICATION);
            getPopGroupsList();
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }


    return (
        <>
            <div className="sub_menu">
                <div className="sub_menu_btns my-1">
                    <button className="btn btn-primary ilo-btn" onClick={() => onActionClick({ value: NEW }, {})}>{t('spComponent.new_btn')}</button> | <span className="ilo-section-title p-1">{t('popGroups.modalTitle')}</span>
                </div>
            </div>
            <div className="container-fluid mt-1">
                {/* modal window */}
                {showModal && <PopGroupsModal type={viewType} data={popGroupData} toggleWindow={toggleWindow} onSave={onSave} />}
                {/* alert window */}
                {showAlert &&
                    <Dialog
                        className="ilo-delete-confirm"
                        title={t('popGroups.delete_title')}
                        onClose={() => toggleAlert()}
                    >
                        <p style={{ margin: "25px", textAlign: "center" }}>{t('popGroups.delete_body', { popgroupname: popGroupData.name })}</p>
                        <DialogActionsBar layout="end">
                            <button className="k-button" onClick={onDelete}>{t('alerts.yes_btn')}</button>
                            <button className="k-button" onClick={toggleAlert}>{t('alerts.no_btn')}</button>
                        </DialogActionsBar>
                    </Dialog>
                }
                {/* Grid */}
                <div className="row">
                    {/* grid */}
                    <div className="container_grid">
                        <PopGroupsGrid data={groupsList} onActionClick={onActionClick} />
                    </div>
                </div>
            </div>
        </>
    );
}


export default PopGroups